import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, MapPin, Award, Clock, Shield, Star, Factory, Warehouse, Building2, Wrench, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ReviewsSchema from '@/components/ReviewsSchema';
import OrganizationSchema from '@/components/schema/OrganizationSchema';
import RelatedCaseStudies from '@/components/RelatedCaseStudies';

const Home = () => {
  const services = [
    {
      title: 'Commercial EPC (NDEPC)',
      description: 'Non-Domestic EPCs for workshops, warehouses, factories, industrial units, offices, shops and pubs. Level 3 SBEM-based, lodged within a week.',
      price: 'From £180',
      location: 'South West UK',
      link: '/commercial-epc',
      featured: true
    },
    {
      title: 'Commercial Energy Advice Report (NDEAR)',
      description: 'Higher-value consultancy for commercial owners who want more than a certificate — fabric, services, controls, renewables and MEES improvement strategy.',
      price: 'From £300',
      location: 'South West UK',
      link: '/services/commercial_advice',
      featured: true
    },
    {
      title: 'MEES & Commercial Improvement Strategy',
      description: 'An EPC tells you where the building is. Our advice helps you understand how to improve it. Staged pathways, 7-year payback modelling, exemption evidence packs.',
      price: 'Included in NDEAR',
      location: 'South West UK',
      link: '/mees-commercial-buildings'
    },
    {
      title: 'SBEM & Building Energy Modelling',
      description: 'iSBEM non-domestic energy modelling for new build, refurbishment feasibility and MEES scenario testing. Nationwide desktop service from drawings.',
      price: 'Individually Quoted',
      location: 'Nationwide',
      link: '/sbem-explained'
    },
    {
      title: 'Industrial / Warehouse / Factory EPCs',
      description: 'One of our most efficient survey types — steel-frame warehouses, workshops, light-industrial and distribution units across the South West.',
      price: 'From £180',
      location: 'South West UK',
      link: '/industrial-epc'
    },
    {
      title: 'Domestic Energy Advice Report (DEAR)',
      description: 'Whole-house energy advice for homeowners planning retrofit — fabric, heating, hot water, ventilation, renewables, sequenced practical improvements.',
      price: 'From £180',
      location: 'South West UK',
      link: '/services/domestic_advice'
    },
    {
      title: 'Domestic EPC',
      description: 'Accredited RdSAP Domestic EPCs across Exeter and Devon for sales, lettings, MEES and mixed-use sites where a dwelling forms part of a commercial property.',
      price: 'From £120',
      location: 'Exeter & Devon',
      link: '/domestic-epc-exeter'
    },
    {
      title: 'SAP Calculations',
      description: 'Design-stage and as-built SAP for new build dwellings, conversions and extensions. Nationwide desktop service.',
      price: 'From £180',
      location: 'Nationwide',
      link: '/sap-calculations'
    },
    {
      title: 'U-Value Calculations',
      description: 'Thermal performance calculations for walls, floors, roofs, windows and doors to Building Regulations Part L.',
      price: 'From £65',
      location: 'Nationwide',
      link: '/u-value-calculations'
    },
    {
      title: 'Lease & Floor Plans',
      description: 'Accurate to-scale lease plans for Land Registry compliance and marketing floor plans for property sales.',
      price: 'From £85',
      location: 'Exeter',
      link: '/lease-plans'
    },
    {
      title: 'Retrofit Coordinator',
      description: 'PAS 2035 Retrofit Coordination and whole-house assessments for domestic properties.',
      price: 'Price on Request',
      location: 'Devon & South West',
      link: '/services/retrofit'
    },
    {
      title: 'Heat Load Calculations',
      description: 'Independent, MCS-compliant heat load calculations for heat pumps. Remote UK-wide or on-site.',
      price: 'From £145',
      location: 'Nationwide',
      link: '/independent-heat-load-calculations'
    }
  ];

  const features = [
    {
      icon: <Award className="w-8 h-8 text-green-600" />,
      title: 'Fully Accredited',
      description: 'Elmhurst Energy, TrustMark, and Quidos certified assessors.'
    },
    {
      icon: <Clock className="w-8 h-8 text-cyan-500" />,
      title: 'Fast Turnaround',
      description: 'Quick booking and fast certificate delivery. Express 48-hour service available.'
    },
    {
      icon: <MapPin className="w-8 h-8 text-green-600" />,
      title: 'Wide Coverage',
      description: 'Serving Exeter, South West UK, and nationwide digital services.'
    },
    {
      icon: <Shield className="w-8 h-8 text-cyan-500" />,
      title: 'Trusted Service',
      description: 'Professional assessments with transparent, competitive pricing.'
    }
  ];

  const testimonials = [
    {
      quote: "I've used Energy Performance Direct on many occasions and the EPC was done speedily and efficiently as usual. Excellent Service 5 Stars.",
      name: "Mark Z.",
      company: "Northwood",
      service: "Domestic EPC",
      source: "FreeIndex"
    },
    {
      quote: "We have used Zak for the past few years and he is prompt and efficient, easy to communicate with and will continue to use him.",
      name: "Damian C.",
      company: "Exeter",
      service: "Commercial EPC",
      source: "FreeIndex"
    },
    {
      quote: "Zak was friendly, professional and efficient. He was able to arrange an appointment very quickly and his turnaround time was fantastic. Thank you very much.",
      name: "Emma G.",
      company: "Newton Abbot",
      service: "Domestic EPC",
      source: "FreeIndex"
    },
    {
      quote: "I had a Commercial EPC, it was arranged and carried out within the week and the results given without delay. Very competitive price.",
      name: "Andrew H.",
      company: "Minehead",
      service: "Commercial EPC",
      source: "FreeIndex"
    },
    {
      quote: "Excellent service from Zak at Energy Performance Direct - faultless.",
      name: "Hugh O.",
      company: "Exeter",
      service: "Domestic EPC",
      source: "FreeIndex"
    },
    {
      quote: "Efficient, friendly, helpful, knowledgeable and easy to contact! 100%.",
      name: "Phil C.",
      company: "Cullompton",
      service: "Energy Assessment",
      source: "FreeIndex"
    }
  ];

  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Independent Building Energy Consultancy | Commercial EPCs, Energy Advice, MEES & SBEM | Energy Performance Direct"
        description="Independent Building Energy Consultancy. Commercial EPCs for workshops, warehouses, factories, offices, shops and industrial units from £180. Commercial and Domestic Energy Advice Reports, MEES strategy and SBEM modelling across the South West and nationwide."
        keywords="commercial EPC, non-domestic EPC, commercial energy assessor, warehouse EPC, workshop EPC, factory EPC, industrial EPC, office EPC, shop EPC, commercial energy advice, MEES, SBEM, domestic energy advice, DEAR, NDEAR, building energy consultancy, energy assessor Exeter Devon Cornwall Somerset Dorset"
        canonical="/"
      />
      <OrganizationSchema />
      <ReviewsSchema />
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50 opacity-60"></div>
        <div className="container mx-auto px-4 py-20 lg:py-28 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-white rounded-full shadow-sm">
              <span className="text-sm font-medium text-green-600">Energy Performance Direct · Independent building energy consultancy</span>
            </div>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
              data-testid="hero-heading"
            >
              Independent Building Energy Consultancy
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-4 max-w-3xl mx-auto">
              Commercial and Domestic energy assessment, modelling and practical improvement advice across the South West and beyond.
            </p>
            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              We specialise in straightforward <strong>Commercial EPCs</strong> for workshops, warehouses, factories, industrial units, offices, shops and hospitality buildings — together with detailed <strong>Commercial and Domestic Energy Advice Reports</strong> for owners who want to understand how their buildings can genuinely perform better.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/booking">
                <Button 
                  data-testid="hero-book-now-btn"
                  size="lg" 
                  className="text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl"
                  style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                >
                  Get a Quote in 2 Minutes
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  data-testid="hero-view-services-btn"
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full"
                >
                  View All Services
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-12 flex flex-wrap justify-center items-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/ejoqsqlu_image.png" 
                alt="Elmhurst Energy" 
                className="h-12"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/gfybwp7x_image.png" 
                alt="TrustMark" 
                className="h-12"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/b08gspqy_image.png" 
                alt="Buy With Confidence" 
                className="h-12"
              />
            </div>

            {/* Review Trust Section */}
            <div className="mt-8">
              <Link to="/reviews" className="inline-block hover:opacity-90 transition-opacity">
                <div className="flex items-center justify-center gap-2 text-white">
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </div>
                  <span className="font-medium">4.9/5 from 200+ reviews</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all card-hover">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From <Link to="/services/commercial_warehouse" className="text-green-600 hover:text-green-700 font-medium">commercial EPC services in Exeter</Link> to nationwide digital calculations, we provide comprehensive energy assessment services including <Link to="/services/commercial_advice" className="text-green-600 hover:text-green-700 font-medium">non-domestic EPC assessments</Link> across Devon, Cornwall, and Somerset.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="border-none shadow-md hover:shadow-2xl transition-all card-hover overflow-hidden group"
                data-testid={`service-card-${index}`}
              >
                <div className="h-2 bg-gradient-to-r from-green-600 to-cyan-500"></div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{service.location}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-green-600 transition-colors">{service.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-green-600">{service.price}</span>
                    <Link to={service.link}>
                      <Button 
                        data-testid={`service-learn-more-btn-${index}`}
                        variant="ghost" 
                        size="sm" 
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        Learn More
                        <ArrowRight className="ml-1 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="view-all-services-btn"
                size="lg" 
                className="text-white px-8 rounded-full hover:opacity-90"
                style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industrial and Warehouse EPC Services Section */}
      <section className="py-20 bg-white" data-testid="industrial-epc-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-full text-sm mb-4">
                <Factory className="w-4 h-4" />
                Specialist Industrial Services
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Industrial and Warehouse EPC Services</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Professional energy assessments for warehouses, factories, workshops and distribution centres across Devon, Cornwall, Somerset and Dorset.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <Link to="/warehouse-epc" className="group" data-testid="industrial-link-warehouse">
                <Card className="border-none shadow-md hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <Warehouse className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-green-600 transition-colors">Warehouse EPCs</h3>
                    <p className="text-sm text-gray-600">Distribution centres, storage and logistics facilities</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/factory-epc" className="group" data-testid="industrial-link-factory">
                <Card className="border-none shadow-md hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <Factory className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-green-600 transition-colors">Factory EPCs</h3>
                    <p className="text-sm text-gray-600">Manufacturing plants and production facilities</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/workshop-epc" className="group" data-testid="industrial-link-workshop">
                <Card className="border-none shadow-md hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <Wrench className="w-10 h-10 text-amber-600 mx-auto mb-3" />
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-green-600 transition-colors">Workshop EPCs</h3>
                    <p className="text-sm text-gray-600">Trade workshops and light industrial units</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/industrial-epc" className="group" data-testid="industrial-link-all">
                <Card className="border-none shadow-md hover:shadow-xl transition-all h-full bg-gray-900 text-white">
                  <CardContent className="p-6 text-center">
                    <Building2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-green-400 transition-colors">All Industrial EPCs</h3>
                    <p className="text-sm text-gray-300">View all industrial EPC services</p>
                  </CardContent>
                </Card>
              </Link>
            </div>

            <div className="text-center">
              <Link to="/industrial-epc">
                <Button
                  data-testid="industrial-epc-learn-more-btn"
                  size="lg"
                  className="text-white px-8 rounded-full bg-gray-900 hover:bg-gray-800"
                >
                  Learn More About Industrial EPCs
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white">Why Choose Energy Performance Direct?</h2>
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1 text-white" />
                <p className="text-base text-white">Fully accredited with Elmhurst Energy, TrustMark, Quidos, and Buy With Confidence</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1 text-white" />
                <p className="text-base text-white">Transparent, competitive pricing for all services</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1 text-white" />
                <p className="text-base text-white">Fast turnaround - certificates delivered within 48 hours</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1 text-white" />
                <p className="text-base text-white">Serving Exeter, South West UK, and nationwide digital services</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1 text-white" />
                <p className="text-base text-white">Experienced assessors delivering excellent customer service</p>
              </div>
            </div>
            <div className="mt-8">
              <Link to="/contact">
                <Button 
                  data-testid="contact-us-btn"
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 rounded-full"
                >
                  Contact Us Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Real Projects, Real Results — case study previews */}
      <RelatedCaseStudies
        title="Real projects, real results"
        subtitle="Every case study below is a real building we've assessed and modelled — period offices, industrial workshops, conversions and London commercial splits. See the starting rating, the staged improvement plan, the costs and the rating we hit."
        limit={3}
      />

      {/* Customer Testimonials Section */}
      <section className="py-20 bg-gray-50" data-testid="testimonials-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Trusted by 200+ Customers</h2>
              <p className="text-base text-gray-600">4.9 out of 5 on Google Reviews</p>
            </div>

            {/* Testimonial Card */}
            <div className="relative bg-white rounded-2xl shadow-lg p-8 md:p-10 min-h-[220px] flex flex-col justify-between">
              <Quote className="w-10 h-10 text-green-200 absolute top-6 left-6" />
              <div className="relative z-10">
                <p className="text-lg md:text-xl text-gray-700 italic leading-relaxed mb-6 pl-8">
                  "{testimonials[activeTestimonial].quote}"
                </p>
                <div className="flex items-center justify-between pl-8">
                  <div>
                    <p className="font-semibold text-gray-900">{testimonials[activeTestimonial].name}</p>
                    <p className="text-sm text-gray-500">
                      {testimonials[activeTestimonial].company} &middot; {testimonials[activeTestimonial].service}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                    via {testimonials[activeTestimonial].source}
                  </span>
                </div>
              </div>

              {/* Dots navigation */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    data-testid={`testimonial-dot-${index}`}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      index === activeTestimonial
                        ? 'bg-green-600 w-6'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="text-center mt-8">
              <Link to="/reviews" className="text-green-600 hover:text-green-700 font-medium text-sm inline-flex items-center gap-1">
                Read all 200+ reviews
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Ready to Get Started?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Book online in 2 minutes. EPC certificates delivered within 48 hours. No VAT to pay.
            </p>
            <Link to="/booking">
              <Button 
                data-testid="cta-book-now-btn"
                size="lg" 
                className="text-white px-12 py-6 text-lg rounded-full shadow-lg hover:shadow-xl"
                style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
              >
                Check Availability
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;